import { defineReceiver } from '@drapejs/invoker';
import { fetchPage } from '@drapejs/core';
import { batch } from '@distancify/drapejs-litium';
import { gql } from 'graphql-request';

import {
  getProductBlockCacheKey,
  isProductsByRelationDataSource,
  isProductsByConditionDataSource,
  inferProductDetailsContext,
} from '@/composables/useProductBlock';

export const scheduleAll = defineReceiver(
  fetchPage,
  async function (command: any, data) {
    if (isProductDetailsPage(data)) {
      const settings = await this.cache.getItem('/settings');

      const blocks = settings?.pdpProductBlocks || [];
      data.productBlocks = blocks
        .filter((b: any) => b.isShownForInactiveProduct)
        .concat(blocks.filter((b: any) => !b.isShownForInactiveProduct));

      await scheduleFetchBlocksData.call(
        this,
        command,
        data,
        data.productBlocks
      );

      await this.cache.setItem(command.cacheKey, data);
    }
    await scheduleFetchBlocksData.call(this, command, data, data.blocks || []);
    return data;
  }
);

async function scheduleFetchBlocksData(command: any, data: any, blocks: any[]) {
  for (let uid in blocks) {
    const block = blocks[uid];

    inferProductDetailsContext(command, data, block);

    if (isProductsByConditionDataSource(block)) {
      await this.invoke(
        batch.scheduleQuery,
        getGetBlockProductsQuery(data, block)
      );
    } else if (isProductsByRelationDataSource(block)) {
      await this.invoke(
        batch.scheduleQuery,
        getProductsByRelationQuery(data, block)
      );
    }
  }
}

function getGetBlockProductsQuery(data: any, block: any) {
  return {
    cacheKey: getProductBlockCacheKey(block),
    query: gql`
productSearchV2(take: $take, filter: $filter) {
  hits
  products {
    url
    fields
    id
    stockStatus
    isMultiPriced
    variantsCount
    selectedVariant {
      id
      fields
      isBundle
      price {
        listPrice
        campaignPrice
      }
      priceFlag
    }
  }
}
  `,
    variables: {
      take: {
        value: +block.numberOfProducts,
        type: 'Int',
      },
      filter: {
        value: (block.filter?.filter || []).map((s: any) => {
          delete s._uid;
          s.conditions.forEach((c: any) => delete c._uid);
          return s;
        }),
        type: '[SegmentInput]',
      },
    },
  };
}

function isProductDetailsPage(data: any) {
  return (
    data.component?.toLowerCase()?.indexOf('product') > 0 || data.articleNumber
  );
}

function getProductsByRelationQuery(data: any, block: any) {
  return {
    cacheKey: getProductBlockCacheKey(block),
    query: gql`
productsByRelation(take: $take, relationName: $relationName, baseProductId: $baseProductId, variantId: $variantId, isShownForInactiveProduct: $isShownForInactiveProduct) {
        articleNumber: id
        brand: brandName
        tag {
          name
          value
        }
        baseProduct {
          id
        }
        productName: variantName
        url
        image
        price {
          originalPrice
          campaignPrice
        }
        priceFlag
        isBundle
        stock {
          totalStockAvailable
          currentStock {
            amount
            date
          }
          incomingStock {
            date
            amount
          }
          expectedShippingDateTime
          deliveryDateFromSupplier
        }
}
  `,
    variables: {
      take: {
        value: block.numberOfProducts || 0,
        type: 'Int',
      },
      relationName: {
        value: block.relation?.relation?.value || '',
        type: 'String',
      },
      baseProductId: {
        value: block.baseProductId || '',
        type: 'String',
      },
      variantId: {
        value: block.variantId || '',
        type: 'String',
      },
      isShownForInactiveProduct: {
        value: block.isShownForInactiveProduct || false,
        type: 'Boolean',
      },
    },
  };
}
